import {
    ApiFullLandingPageLink,
    ApiShopperLandingPageLink,
    toApiFullLandingPageLink,
} from '@storefronts/common-types'
import { useContext, useEffect, useMemo } from 'react'
import { LandingPageLinksContext } from '../context/LandingPageLinksContext'

/**
 * Resolves a Full link for a shopper CARD/Full snapshot. Looks up the current
 * cache (so hydration can replace a stale CARD) and batch-fetches Full when
 * the hash is still CARD or missing.
 */
export function useFullLandingPageLink(
    link: ApiShopperLandingPageLink | null | undefined,
): ApiFullLandingPageLink | null {
    const { getCachedOrderedLinksForHashes, fetchAndCacheLinksForHashes } =
        useContext(LandingPageLinksContext)

    // Callers pass a new object each render; key by hash so the fetch effect
    // does not re-run unless the product actually changes.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    const hash = link?.linkHash ?? ''
    const hashes = useMemo(() => (hash === '' ? [] : [hash]), [hash])

    const cached = hashes.length
        ? getCachedOrderedLinksForHashes(hashes).orderedLinks[0]
        : undefined
    const cachedFullLink = toApiFullLandingPageLink(cached ?? null)

    useEffect(() => {
        if (hashes.length === 0 || cachedFullLink) return
        void fetchAndCacheLinksForHashes(hashes)
    }, [hashes, cachedFullLink, fetchAndCacheLinksForHashes])

    return cachedFullLink ?? toApiFullLandingPageLink(link ?? null)
}
